<template>
  <div>
    <base-material-card
      icon="mdi-file-document-multiple"
      title="บันทึกรายงานพนักงานราชการ"
      class="px-5 py-3"
    >
      <!-- Stepper Navigation -->
      <!-- Check for existing report -->

      <v-alert
        v-if="!existingReportWarning || !existingReportWarning.status"
        prominent
        type="error"
        color="red darken-2"
        border="left"
        elevation="4"
        class="mb-6 white--text animate-pulse"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-h6 font-weight-bold">
              🔴 ระบบเปิดให้รายงานข้อมูลแล้ว
            </div>
            <div>
              กรุณาดำเนินการบันทึกรายงาน
              <strong>{{ currentPeriodLabel }}</strong>
              ให้เสร็จสิ้นภายในระยะเวลาที่กำหนด เพื่อความถูกต้องของงบประมาณ
            </div>
          </v-col>
          <v-col cols="auto">
            <v-chip color="white" text-color="red darken-2" font-weight-bold>
              สถานะ: ยังไม่ได้รายงาน
            </v-chip>
          </v-col>
        </v-row>
      </v-alert>

      <div class="text-right">
        <v-btn outlined @click="checkExistingReport()" color="info">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Alert for existing rejected/cancelled report -->
      <v-alert
        v-if="
          existingReportWarning &&
            (existingReportWarning.status === 'rejected' ||
              existingReportWarning.status === 'cancelled')
        "
        type="warning"
        outlined
        class="mb-4"
      >
        <div class="mt-2">
          ⚠️ ข้อมูลรายงานได้รับการ
          <v-chip
            :color="getStatusColor(existingReportWarning.status)"
            text-color="white"
            small
            class="mx-1"
          >
            {{ getStatusText(existingReportWarning.status) }}
          </v-chip>
          จาก ส่วนกลาง ให้สถานศึกษาดำเนินการแก้ไขรายการใหม่อีกครั้ง
        </div>
        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="mb-3">
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-2">
                  <v-icon color="error" small class="mr-2">
                    mdi-file-pdf
                  </v-icon>
                  <span class="font-weight-bold text-body2">
                    หนังสือนำส่ง
                  </span>
                </div>
                <div class="text-body2 text-grey ml-6 mb-3">
                  {{ existingReportWarning.letterFile || "ไม่ได้อัปโหลด" }}
                </div>
                <v-btn
                  v-if="existingReportWarning.letterFile"
                  small
                  outlined
                  color="primary"
                  @click="downloadFile(existingReportWarning, 'letter')"
                  :loading="isDownloadingFile"
                  class="mt-2"
                >
                  <v-icon small class="mr-1">mdi-file-find</v-icon>
                  ดูหนังสือนำส่ง
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card outlined class="mb-3">
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-2">
                  <v-icon color="error" small class="mr-2">
                    mdi-file-pdf
                  </v-icon>
                  <span class="font-weight-bold text-body2">
                    ไฟล์รายงาน
                  </span>
                </div>
                <div class="text-body2 text-grey ml-6 mb-3">
                  {{ existingReportWarning.report_file || "ไม่ได้อัปโหลด" }}
                </div>
                <v-btn
                  v-if="existingReportWarning.report_file"
                  small
                  outlined
                  color="primary"
                  @click="downloadFile(existingReportWarning, 'report')"
                  :loading="isDownloadingFile"
                  class="mt-2"
                >
                  <v-icon small class="mr-1">mdi-file-find</v-icon>
                  ดูไฟล์รายงาน
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <!-- Action Buttons รอการอนุมัติ -->
        <div class="d-flex gap-2 justify-end mt-4">
          <v-dialog v-model="showCancelConfirm" max-width="500px" persistent>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                small
                outlined
                color="error"
                v-bind="attrs"
                v-on="on"
                title="ลบรายการเพื่อเริ่มทำใหม่"
              >
                <v-icon small class="mr-1">mdi-trash-can</v-icon>
                ลบรายการ
              </v-btn>
            </template>

            <v-card>
              <v-card-title class="text-h6">
                ยืนยันการลบรายการ
              </v-card-title>

              <v-card-text class="mt-2">
                <p class="font-weight-bold">
                  ⚠️ คุณแน่ใจหรือว่าต้องการลบรายการนี้?
                </p>
                <p class="text-body2 text-grey mt-2">
                  การลบนี้จะลบข้อมูลรายงานทั้งหมดออกจากระบบ
                  คุณจะต้องเริ่มทำรายการใหม่อีกครั้ง
                </p>
                <v-alert type="info" outlined class="mt-4">
                  <strong>เหตุผล:</strong>
                  รายการที่อยู่สถานะ "รอการอนุมัติ"
                  ยังไม่ได้รับการตรวจสอบจากส่วนกลาง คุณสามารถลบและทำใหม่ได้ทันที
                </v-alert>
              </v-card-text>

              <v-divider />

              <v-card-actions>
                <v-spacer />
                <v-btn color="grey" text @click="showCancelConfirm = false">
                  ยกเลิก
                </v-btn>
                <v-btn
                  color="error"
                  @click="cancelReport"
                  :loading="isCancellingReport"
                >
                  ยืนยันการลบ
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-alert>

      <!-- Alert for existing submitted/approved report -->
      <v-alert
        v-if="
          existingReportWarning &&
            (existingReportWarning.status === 'submitted' ||
              existingReportWarning.status === 'approved')
        "
        type="warning"
        outlined
        class="mb-4"
      >
        <strong>⚠️ ข้อมูลรายงานที่มีอยู่แล้ว</strong>
        <div class="mt-2">
          มีรายงานปีและงวดนี้ในสถานะ
          <v-chip
            :color="getStatusColor(existingReportWarning.status)"
            text-color="white"
            small
            class="mx-1"
          >
            {{ getStatusText(existingReportWarning.status) }}
          </v-chip>
          แล้ว
        </div>

        <!-- Files Section -->
        <div class="mt-4">
          <v-divider class="my-3" />
          <div class="font-weight-bold mb-3">📄 ไฟล์ที่อัปโหลด</div>

          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined class="mb-3">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="error" small class="mr-2">
                      mdi-file-pdf
                    </v-icon>
                    <span class="font-weight-bold text-body2">
                      หนังสือนำส่ง
                    </span>
                  </div>
                  <div class="text-body2 text-grey ml-6 mb-3">
                    {{ existingReportWarning.letterFile || "ไม่ได้อัปโหลด" }}
                  </div>
                  <v-btn
                    v-if="existingReportWarning.letterFile"
                    small
                    outlined
                    color="primary"
                    @click="downloadFile(existingReportWarning, 'letter')"
                    :loading="isDownloadingFile"
                    class="mt-2"
                  >
                    <v-icon small class="mr-1">mdi-file-find</v-icon>
                    ดูหนังสือนำส่ง
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined class="mb-3">
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-2">
                    <v-icon color="error" small class="mr-2">
                      mdi-file-pdf
                    </v-icon>
                    <span class="font-weight-bold text-body2">
                      ไฟล์รายงาน
                    </span>
                  </div>
                  <div class="text-body2 text-grey ml-6 mb-3">
                    {{ existingReportWarning.report_file || "ไม่ได้อัปโหลด" }}
                  </div>
                  <v-btn
                    v-if="existingReportWarning.report_file"
                    small
                    outlined
                    color="primary"
                    @click="downloadFile(existingReportWarning, 'report')"
                    :loading="isDownloadingFile"
                    class="mt-2"
                  >
                    <v-icon small class="mr-1">mdi-file-find</v-icon>
                    ดูไฟล์รายงาน
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-3" />

          <!-- Action Buttons รอการอนุมัติ -->
          <div class="d-flex gap-2 justify-end mt-4">
            <v-dialog v-model="showCancelConfirm" max-width="500px" persistent>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="existingReportWarning.status === 'submitted'"
                  small
                  outlined
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  title="ลบรายการเพื่อเริ่มทำใหม่"
                >
                  <v-icon small class="mr-1">mdi-trash-can</v-icon>
                  ลบรายการ
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="text-h6">
                  ยืนยันการลบรายการ
                </v-card-title>

                <v-card-text class="mt-2">
                  <p class="font-weight-bold">
                    ⚠️ คุณแน่ใจหรือว่าต้องการลบรายการนี้?
                  </p>
                  <p class="text-body2 text-grey mt-2">
                    การลบนี้จะลบข้อมูลรายงานทั้งหมดออกจากระบบ
                    คุณจะต้องเริ่มทำรายการใหม่อีกครั้ง
                  </p>
                  <v-alert type="info" outlined class="mt-4">
                    <strong>เหตุผล:</strong>
                    รายการที่อยู่สถานะ "รอการอนุมัติ"
                    ยังไม่ได้รับการตรวจสอบจากส่วนกลาง
                    คุณสามารถลบและทำใหม่ได้ทันที
                  </v-alert>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                  <v-spacer />
                  <v-btn color="grey" text @click="showCancelConfirm = false">
                    ยกเลิก
                  </v-btn>
                  <v-btn
                    color="error"
                    @click="cancelReport"
                    :loading="isCancellingReport"
                  >
                    ยืนยันการลบ
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>

          <!-- Action Buttons อนุมัติแล้วแต่ขอยกเลิก-->
          <div class="d-flex gap-2 justify-end mt-4">
            <v-dialog
              v-model="showCancelApproveConfirm"
              max-width="500px"
              persistent
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="existingReportWarning.status === 'approved'"
                  small
                  outlined
                  color="warning"
                  v-bind="attrs"
                  v-on="on"
                  title="ขอยกเลิกรายการที่อนุมัติแล้ว"
                >
                  <v-icon small class="mr-1">mdi-cancel</v-icon>
                  ขอยกเลิก
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="text-h6">
                  ยืนยันการขอยกเลิก
                </v-card-title>

                <v-card-text class="mt-2">
                  <p class="font-weight-bold">
                    ⚠️ คุณแน่ใจหรือว่าต้องการขอยกเลิกรายการนี้?
                  </p>
                  <p class="text-body2 text-grey mt-2">
                    การขอยกเลิกนี้จะส่งไปให้ส่วนกลางตรวจสอบและอนุมัติ
                  </p>
                  <v-alert type="info" outlined class="mt-4">
                    <strong>ขั้นตอน:</strong>
                    <ol class="mt-2 ml-4">
                      <li>ส่งคำขอยกเลิกไปยังส่วนกลาง</li>
                      <li>รอส่วนกลางตรวจสอบ</li>
                      <li>รอการอนุมัติการยกเลิก</li>
                      <li>หากอนุมัติแล้ว คุณจะสามารถสร้างรายการใหม่ได้</li>
                    </ol>
                  </v-alert>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="grey"
                    text
                    @click="showCancelApproveConfirm = false"
                  >
                    ปิด
                  </v-btn>
                  <v-btn
                    color="warning"
                    @click="cancelReportApprove"
                    :loading="isCancellingReport"
                  >
                    ยืนยันการขอยกเลิก
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </div>
        </div>
      </v-alert>

      <!-- Updated alert for submitted status -->
      <v-alert
        v-if="
          existingReportWarning && existingReportWarning.status === 'submitted'
        "
        type="warning"
        outlined
        class="mb-4"
      >
        <strong>⏳ รายงานรอการอนุมัติ</strong>
        <div class="mt-2">
          รายงานนี้ได้ถูกส่งแล้ว และรอการตรวจสอบจากส่วนกลาง
        </div>
        <div class="mt-2 text-body2">
          <v-chip
            :color="getStatusColor(existingReportWarning.status)"
            text-color="white"
            small
            class="mx-1"
          >
            {{ getStatusText(existingReportWarning.status) }}
          </v-chip>
        </div>
        <div class="mt-3 text-body2">
          ✏️
          <strong>หากต้องการแก้ไข:</strong>
          คุณสามารถลบรายการและสร้างใหม่ได้ (กดปุ่ม "ลบรายการ" ด้านล่าง)
        </div>
      </v-alert>

      <!-- Updated alert for checking status -->
      <v-alert
        v-if="
          existingReportWarning && existingReportWarning.status === 'checking'
        "
        type="info"
        outlined
        class="mb-4"
      >
        <strong>🔍 รายงานกำลังตรวจสอบ</strong>
        <div class="mt-2">
          รายงานนี้อยู่ระหว่างการตรวจสอบจากส่วนกลาง กรุณารอผลการตรวจสอบ
        </div>
        <div class="mt-2 text-body2">
          <v-chip
            :color="getStatusColor(existingReportWarning.status)"
            text-color="white"
            small
            class="mx-1"
          >
            {{ getStatusText(existingReportWarning.status) }}
          </v-chip>
        </div>
      </v-alert>

      <!-- Updated alert for approved status -->
      <v-alert
        v-if="
          existingReportWarning && existingReportWarning.status === 'approved'
        "
        type="success"
        outlined
        class="mb-4"
      >
        <strong>✅ รายงานอนุมัติแล้ว</strong>
        <div class="mt-2">
          รายงานนี้ได้รับการอนุมัติจากส่วนกลางแล้ว
        </div>
        <div class="mt-2 text-body2">
          <v-chip
            :color="getStatusColor(existingReportWarning.status)"
            text-color="white"
            small
            class="mx-1"
          >
            {{ getStatusText(existingReportWarning.status) }}
          </v-chip>
        </div>
        <div class="mt-3 text-body2">
          ❌
          <strong>หากต้องการยกเลิก:</strong>
          โปรดกดปุ่ม "ขอยกเลิก" ด้านล่าง (ต้องรอการอนุมัติจากส่วนกลาง)
        </div>
      </v-alert>

      <!-- Main Stepper - Only show when no existing report or it can be edited -->
      <v-stepper
        v-model="currentStep"
        class="elevation-10 rounded-xl overflow-hidden custom-stepper"
        v-if="!existingReportWarning || !existingReportWarning.status"
      >
        <v-stepper-header class="blue darken-4">
          <v-stepper-step
            :complete="currentStep > 1"
            step="1"
            editable
            color="secondary"
            class="white--text"
          >
            <span class="white--text">ข้อมูลรายงาน</span>
          </v-stepper-step>
          <v-divider dark />

          <v-stepper-step
            :complete="currentStep > 2"
            step="2"
            editable
            color="secondary"
            class="white--text"
          >
            <span class="white--text">งบประมาณรายเดือน</span>
          </v-stepper-step>

          <v-divider />

          <v-stepper-step
            :complete="currentStep > 3"
            step="3"
            editable
            color="secondary"
            class="white--text"
          >
            <span class="white--text">หนังสือนำส่ง</span>
          </v-stepper-step>

          <v-divider />

          <v-stepper-step
            :complete="currentStep > 4"
            step="4"
            editable
            color="secondary"
            class="white--text"
          >
            <span class="white--text">ไฟล์รายงาน</span>
          </v-stepper-step>

          <v-divider />

          <v-stepper-step step="5">
            <span class="white--text">ยืนยันและส่ง</span></v-stepper-step
          >
        </v-stepper-header>

        <!-- STEP 1: Report Information -->
        <v-stepper-content step="1">
          <v-card class="mb-6" elevation="2">
            <v-card-title class="font-weight-bold pb-2">
              <v-icon class="mr-2">mdi-information</v-icon>
              ข้อมูลรายงาน
            </v-card-title>
            <v-divider />

            <v-card-text class="pa-6">
              <v-form ref="reportForm" v-model="reportFormValid">
                <!-- College Information (Read-only) -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6" class="text-center">
                    รหัสสถานศึกษา : {{ reportData.college_code }}
                  </v-col>
                  <v-col cols="12" md="6" class="text-center">
                    ชื่อวิทยาลัย : {{ reportData.college_name }}
                  </v-col>
                </v-row>

                <!-- Reporting Period Info -->
                <v-row class="mb-4">
                  <v-col
                    cols="12"
                    class="text-center font-weight-bold bg-blue-lighten-5 pa-3 rounded"
                  >
                    <v-icon small class="mr-1">mdi-calendar</v-icon>
                    {{ currentPeriodLabel }}
                  </v-col>
                </v-row>

                <!-- Employee Count & Director Info -->
                <v-row class="mb-4">
                  <v-col cols="12" md="6" class="text-center">
                    จำนวนกรอบอัตรากำลัง : {{ count_manpower }}
                  </v-col>
                  <v-col cols="12" md="6" class="text-center">
                    จำนวนพนักงานที่มี : {{ reportData.total_employees }}
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="reportData.director_name"
                      label="ชื่อผู้อำนวยการ"
                      outlined
                      dense
                      :rules="[v => !!v || 'จำเป็นต้องระบุ']"
                      counter="255"
                      maxlength="255"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="reportData.director_title"
                      label="ตำแหน่งผู้อำนวยการ"
                      outlined
                      dense
                      :rules="[v => !!v || 'จำเป็นต้องระบุ']"
                      counter="255"
                      maxlength="255"
                    />
                  </v-col>
                </v-row>
              </v-form>

              <v-divider class="my-4" />

              <div class="text-right">
                <v-btn
                  color="success"
                  x-large
                  block
                  :disabled="!reportFormValid"
                  @click="proceedToStep2"
                  class="mt-4 font-weight-bold elevation-5 pulse-button"
                >
                  ดำเนินการขั้นตอนถัดไป
                  <v-icon right>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-stepper-content>

        <!-- STEP 2: Monthly Budget -->
        <v-stepper-content step="2">
          <v-card class="mb-6" elevation="2">
            <v-card-title class="font-weight-bold pb-2">
              <v-icon class="mr-2">mdi-calculator</v-icon>
              รายงานข้อมูลงบประมาณรายเดือน
            </v-card-title>
            <v-divider />

            <v-card-text class="pa-6">
              <v-alert type="info" outlined class="mb-6">
                <strong>งวดรายงาน:</strong>
                {{ currentPeriodLabel }}
                <div class="mt-2 text-body2">
                  {{ currentPeriodMonthsLabel }}
                </div>
                <div class="mt-2 text-body2">
                  กรุณารายงานข้อมูลการเบิกจ่ายงบประมาณเงินค่าตอบแทนพนักงานราชการสำหรับแต่ละเดือน
                </div>
                <div class="mt-2 text-body2 font-weight-bold text-orange">
                  💡 หมายเหตุ:
                  ยอดคงเหลือจากเดือนก่อนหน้าจะนำไปคำนวณต่อในเดือนถัดไป
                </div>
              </v-alert>

              <v-form ref="budgetForm" v-model="budgetFormValid">
                <!-- Display months in this period -->
                <div class="mb-6">
                  <h4 class="font-weight-bold mb-4">
                    <v-icon small class="mr-1">mdi-folder-multiple</v-icon>
                    รายการเบิกจ่ายรายเดือน
                  </h4>

                  <!-- Overall Allocated Budget for Period -->
                  <div class="mb-6 p-4 bg-blue-lighten-5 rounded">
                    <label class="text-subtitle2 font-weight-bold d-block mb-3">
                      <v-icon small class="mr-2">mdi-currency-usd</v-icon>
                      งบประมาณต่องวด
                    </label>
                    <v-text-field
                      v-model.number="reportData.period_allocated_budget"
                      type="number"
                      outlined
                      dense
                      prefix="฿"
                      placeholder="เช่น 100000"
                      :rules="[
                        v => (v !== null && v !== '') || 'จำเป็นต้องระบุ',
                        v => !isNaN(v) || 'ต้องเป็นตัวเลขเท่านั้น'
                      ]"
                      @change="updateAllMonthsAllocatedBudget()"
                    />
                    <div class="mt-2 text-body2 text-grey">
                      ยอดคงเหลือทั้งงวด:
                      <strong class="text-primary">
                        ฿{{ formatCurrency(totalPeriodRemaining) }}
                      </strong>
                    </div>
                  </div>

                  <!-- Monthly Budget Cards -->
                  <v-row>
                    <v-col
                      v-for="month in periodMonths"
                      :key="month.value"
                      cols="12"
                      md="6"
                      lg="4"
                    >
                      <v-card outlined class="monthly-budget-card">
                        <v-card-title class="text-h2 font-weight-bold pa-4">
                         
                         <label class="text-subtitle2 font-weight-bold success--text">
                             <v-icon small class="mr-2">mdi-calendar-month</v-icon> เดือน {{ month.text }}
                            </label> 
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-4">
                          <!-- Allocated Budget (Read-only) -->
                          <div class="mb-3">
                            <label class="text-subtitle2 font-weight-bold">
                              งบประมาณจัดสรร (บาท)
                            </label>
                            <v-text-field
                              :value="
                                getMonthlyBudgetValue(month.value, 'allocated')
                              "
                              type="number"
                              outlined
                              dense
                              prefix="฿"
                              readonly
                              class="bg-grey-lighten-3"
                            />
                            <small class="text-grey">
                              จากงบประมาณทั้งงวด ÷
                              {{ periodMonths.length }} เดือน
                            </small>
                          </div>

                          <!-- Previous Month Remaining (Display) -->
                          <div
                            v-if="getPreviousMonthRemaining(month.value) > 0"
                            class="mb-3 p-2 bg-orange-lighten-5 rounded"
                          >
                            <label
                              class="text-subtitle2 font-weight-bold text-orange"
                            >
                              ยอดคงเหลือจากเดือนก่อน (บาท)
                            </label>
                            <div class="text-h6 font-weight-bold text-orange">
                              ฿{{
                                formatCurrency(
                                  getPreviousMonthRemaining(month.value)
                                )
                              }}
                            </div>
                          </div>

                          <!-- Disbursed Budget -->
                          <div class="mb-3">
                            <label class="text-subtitle2 font-weight-bold">
                              งบประมาณที่เบิกจ่ายแล้ว (บาท)
                            </label>
                            <v-text-field
                              :value="
                                getMonthlyBudgetValue(month.value, 'disbursed')
                              "
                              type="number"
                              outlined
                              dense
                              prefix="฿"
                              placeholder="0"
                              :rules="[
                                v =>
                                  (v !== null && v !== '') || 'จำเป็นต้องระบุ',
                                v => !isNaN(v) || 'ต้องเป็นตัวเลขเท่านั้น'
                              ]"
                              @input="
                                onMonthlyDisbursedChange(month.value, $event)
                              "
                              @change="calculateMonthlyRemaining(month.value)"
                            />
                            <small class="text-grey">
                              ได้รับจัดสรร + ยอดคงเหลือจากเดือนก่อน = ฿{{
                                formatCurrency(
                                  getTotalAvailableBudget(month.value)
                                )
                              }}
                            </small>
                          </div>

                          <!-- Total Available Budget (Display) -->
                          <div class="mb-3 p-2 bg-light rounded">
                            <label class="text-subtitle2 font-weight-bold">
                              งบประมาณที่มีให้ใช้ (บาท)
                            </label>
                            <div class="text-h6 font-weight-bold text-primary">
                              ฿{{
                                formatCurrency(
                                  getTotalAvailableBudget(month.value)
                                )
                              }}
                            </div>
                          </div>

                          <!-- Remaining Budget (Auto-calculated) -->
                          <div class="mb-3">
                            <label class="text-subtitle2 font-weight-bold">
                              งบประมาณที่เหลือ (บาท)
                            </label>
                            <v-text-field
                              :value="
                                getMonthlyBudgetValue(month.value, 'remaining')
                              "
                              type="number"
                              outlined
                              dense
                              prefix="฿"
                              readonly
                              :style="getRemainingStyling(month.value)"
                            />
                          </div>

                          <!-- Percentage bar -->
                          <div class="mt-4">
                            <div class="text-caption mb-1">
                              อัตราการเบิกจ่าย:
                              <strong
                                >{{
                                  getDisbursePercentage(month.value)
                                }}%</strong
                              >
                            </div>
                            <v-progress-linear
                              :value="getDisbursePercentage(month.value)"
                              height="6"
                              color="primary"
                            />
                          </div>

                          <!-- Monthly Notes -->
                          <div class="mt-4">
                            <label
                              class="text-caption font-weight-bold d-block mb-2"
                            >
                              หมายเหตุ/ข้อเสนอแนะ
                            </label>
                            <v-textarea
                              :value="
                                getMonthlyBudgetValue(month.value, 'notes')
                              "
                              outlined
                              dense
                              rows="2"
                              placeholder="เช่น ปัญหา ข้อสังเกต ฯลฯ"
                              counter="200"
                              maxlength="200"
                              @input="onMonthlyNotesChange(month.value, $event)"
                            />
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Summary of Budget -->
                <v-divider class="my-6" />

                <div class="summary-section">
                  <h4 class="font-weight-bold mb-3">
                    <v-icon small class="mr-1">mdi-chart-line</v-icon>
                    สรุปงบประมาณรวมทั้งงวด
                  </h4>

                  <v-row class="mb-4">
                    <v-col cols="12" md="6" lg="3">
                      <v-card outlined class="summary-card">
                        <v-card-text class="pa-4">
                          <div class="text-caption text-grey">
                            งบประมาณทั้งงวด
                          </div>
                          <div class="text-h5 font-weight-bold text-primary">
                            ฿{{
                              formatCurrency(reportData.period_allocated_budget)
                            }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6" lg="3">
                      <v-card outlined class="summary-card">
                        <v-card-text class="pa-4">
                          <div class="text-caption text-grey">
                            เบิกจ่ายทั้งงวด
                          </div>
                          <div class="text-h5 font-weight-bold text-green">
                            ฿{{ formatCurrency(totalDisbursedbBudget) }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6" lg="3">
                      <v-card outlined class="summary-card">
                        <v-card-text class="pa-4">
                          <div class="text-caption text-grey">
                            เหลือทั้งงวด
                          </div>
                          <div
                            class="text-h5 font-weight-bold"
                            :class="
                              totalRemainingBudget >= 0
                                ? 'text-orange'
                                : 'text-red'
                            "
                          >
                            ฿{{ formatCurrency(totalRemainingBudget) }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="6" lg="3">
                      <v-card outlined class="summary-card">
                        <v-card-text class="pa-4">
                          <div class="text-caption text-grey">
                            อัตราเบิกจ่าย
                          </div>
                          <div class="text-h5 font-weight-bold text-info">
                            {{ overallDisbursePercentage }}%
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Overall Progress -->
                  <v-card outlined class="mb-4">
                    <v-card-text class="pa-4">
                      <div class="mb-2">
                        <span class="text-subtitle2 font-weight-bold">
                          ความคืบหน้าการเบิกจ่ายรวมทั้งงวด
                        </span>
                      </div>
                      <v-progress-linear
                        :value="overallDisbursePercentage"
                        height="12"
                        color="primary"
                      />
                    </v-card-text>
                  </v-card>
                </div>
              </v-form>

              <v-divider class="my-4" />

              <div class="d-flex justify-space-between">
                <v-btn color="secondary" @click="currentStep = 1">
                  <v-icon class="mr-2">mdi-arrow-left</v-icon>
                  ย้อนกลับ
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!budgetFormValid"
                  @click="proceedToStep3"
                >
                  ถัดไป
                  <v-icon class="ml-2">mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-stepper-content>

        <!-- STEP 3: Letter of Transmittal -->
        <v-stepper-content step="3">
          <v-card class="mb-6" elevation="2">
            <v-card-title class="font-weight-bold pb-2">
              <v-icon class="mr-2">mdi-file-document</v-icon>
              หนังสือนำส่ง
            </v-card-title>
            <v-divider />

            <v-card-text class="pa-6">
              <v-alert type="info" outlined class="mb-4">
                <strong>หนังสือนำส่งรายงานพนักงานราชการ</strong>
                <div class="mt-2 text-body2">
                  <v-btn
                    color="primary"
                    outlined
                    @click="downloadLetterTemplate"
                  >
                    <v-icon class="mr-2">mdi-download</v-icon>
                    ดาวน์โหลดแบบฟอร์มหนังสือนำส่ง
                  </v-btn>
                  โปรดเตรียมหนังสือนำส่งที่มีลายเซ็นต์ของผู้อำนวยการและอัปโหลดไฟล์
                  PDF ขนาดไม่เกิน 10MB ประเภท PDF เท่านั้น
                </div>
              </v-alert>

              <v-form ref="letterForm" v-model="letterFormValid">
                <v-row class="mb-4">
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="reportData.letter_date"
                      label="วันที่เอกสารนำส่ง"
                      type="date"
                      outlined
                      dense
                      :rules="[v => !!v || 'จำเป็นต้องระบุ']"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="reportData.letter_number"
                      label="เลขที่เอกสารนำส่ง (ถ้ามี)"
                      outlined
                      dense
                      placeholder="เช่น ที่ ตย/0001/2567"
                      counter="100"
                      maxlength="100"
                    />
                  </v-col>
                </v-row>

                <v-row class="mb-4">
                  <v-col cols="12">
                    <v-textarea
                      v-model="reportData.letter_description"
                      label="หมายเหตุ/รายละเอียดเพิ่มเติม"
                      outlined
                      dense
                      rows="3"
                      counter="500"
                      maxlength="500"
                      placeholder="เช่น การขอตรวจสอบข้อมูลพนักงานราชการ ฯลฯ"
                    />
                  </v-col>
                </v-row>

                <v-row class="mb-4">
                  <v-col cols="12">
                    <div class="mb-3">
                      <label class="font-weight-bold d-block mb-2">
                        <v-icon small class="mr-1">mdi-file-pdf</v-icon>
                        ไฟล์หนังสือนำส่ง PDF
                      </label>
                      <small class="text-grey">
                        ขนาดสูงสุด: 10MB | ประเภท: PDF
                      </small>
                    </div>

                    <v-file-input
                      ref="letterFileInput"
                      v-model="letterFile"
                      accept="application/pdf"
                      label="คลิกเพื่อเลือกหรือลากไฟล์"
                      outlined
                      dense
                      show-size
                      @change="validateLetterFile"
                      :rules="[
                        v =>
                          !v || v.size <= 10 * 1024 * 1024 || 'ไฟล์ใหญ่เกินไป'
                      ]"
                      prepend-icon="mdi-file-pdf"
                    />

                    <div v-if="letterFile" class="mt-3 p-3 bg-light rounded">
                      <v-row class="align-center">
                        <v-col cols="auto">
                          <v-icon color="error" large>mdi-file-pdf</v-icon>
                        </v-col>
                        <v-col>
                          <div class="font-weight-bold">
                            {{ letterFile.name }}
                          </div>
                          <div class="text-body2 text-grey">
                            {{ formatFileSize(letterFile.size) }}
                          </div>
                        </v-col>
                        <v-col cols="auto">
                          <v-btn
                            icon
                            small
                            @click="clearLetterFile"
                            title="ลบไฟล์"
                          >
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>
              </v-form>

              <v-divider class="my-4" />

              <div class="d-flex justify-space-between">
                <v-btn color="secondary" @click="currentStep = 2">
                  <v-icon class="mr-2">mdi-arrow-left</v-icon>
                  ย้อนกลับ
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!letterFormValid || !letterFile"
                  @click="proceedToStep4"
                >
                  ถัดไป
                  <v-icon class="ml-2">mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-stepper-content>

        <!-- STEP 4: Report File Upload -->
        <v-stepper-content step="4">
          <v-card class="mb-6" elevation="2">
            <v-card-title class="font-weight-bold pb-2">
              <v-icon class="mr-2">mdi-file-pdf</v-icon>
              ไฟล์รายงานพนักงานราชการ
            </v-card-title>
            <v-divider />

            <v-card-text class="pa-6">
              <v-alert type="info" outlined class="mb-4">
                <strong>ไฟล์รายงานพนักงานราชการตามกรอบอัตรากำลัง</strong>
                <div class="mt-2 text-body2">
                  <v-btn
                    outlined
                    color="primary"
                    rounded
                    class="text-capitalize"
                    @click="openExportDialog"
                  >
                    <v-icon left>mdi-file-pdf-box</v-icon> ส่งออก PDF
                  </v-btn>

                  โปรดเตรียมไฟล์รายงาน PDF ที่มีลายเซ็นต์ของผู้อำนวยการ
                  โดยไฟล์ต้องมีข้อมูลรายชื่อและรายละเอียดพนักงานราชการทั้งหมด
                </div>
              </v-alert>

              <v-form ref="reportFileForm" v-model="reportFileFormValid">
                <v-row class="mb-4">
                  <v-col cols="12">
                    <div class="mb-3">
                      <label class="font-weight-bold d-block mb-2">
                        <v-icon small class="mr-1">mdi-file-pdf</v-icon>
                        ไฟล์รายงาน PDF (ลายเซ็นต์)
                      </label>
                      <small class="text-grey">
                        ขนาดสูงสุด: 10MB | ประเภท: PDF
                      </small>
                    </div>

                    <v-file-input
                      ref="reportFileInput"
                      v-model="reportFile"
                      accept="application/pdf"
                      label="คลิกเพื่อเลือกหรือลากไฟล์"
                      outlined
                      dense
                      show-size
                      @change="validateReportFile"
                      :rules="[
                        v =>
                          !v || v.size <= 10 * 1024 * 1024 || 'ไฟล์ใหญ่เกินไป'
                      ]"
                      prepend-icon="mdi-file-pdf"
                    />

                    <div v-if="reportFile" class="mt-3 p-3 bg-light rounded">
                      <v-row class="align-center">
                        <v-col cols="auto">
                          <v-icon color="error" large>mdi-file-pdf</v-icon>
                        </v-col>
                        <v-col>
                          <div class="font-weight-bold">
                            {{ reportFile.name }}
                          </div>
                          <div class="text-body2 text-grey">
                            {{ formatFileSize(reportFile.size) }}
                          </div>
                        </v-col>
                        <v-col cols="auto">
                          <v-btn
                            icon
                            small
                            @click="clearReportFile"
                            title="ลบไฟล์"
                          >
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <div class="mt-3">
                        <v-btn
                          small
                          outlined
                          color="primary"
                          @click="previewReportPDF"
                          :loading="isPreviewingPDF"
                        >
                          <v-icon small class="mr-1">mdi-eye</v-icon>
                          ดูตัวอย่าง PDF
                        </v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-row class="mb-4">
                  <v-col cols="12">
                    <v-textarea
                      v-model="reportData.report_description"
                      label="รายละเอียดไฟล์รายงาน"
                      outlined
                      dense
                      rows="3"
                      counter="500"
                      maxlength="500"
                      placeholder="เช่น ข้อมูลพนักงานราชการปัจจุบัน ฯลฯ"
                    />
                  </v-col>
                </v-row>
              </v-form>

              <v-divider class="my-4" />

              <div class="d-flex justify-space-between">
                <v-btn color="secondary" @click="currentStep = 3">
                  <v-icon class="mr-2">mdi-arrow-left</v-icon>
                  ย้อนกลับ
                </v-btn>
                <v-btn
                  color="primary"
                  :disabled="!reportFileFormValid || !reportFile"
                  @click="proceedToStep5"
                >
                  ถัดไป
                  <v-icon class="ml-2">mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-stepper-content>

        <!-- STEP 5: Review and Submit -->
        <v-stepper-content step="5">
          <v-card class="mb-6" elevation="2">
            <v-card-title class="font-weight-bold pb-2">
              <v-icon class="mr-2">mdi-clipboard-check</v-icon>
              ยืนยันและส่งรายงาน
            </v-card-title>
            <v-divider />

            <v-card-text class="pa-6">
              <!-- Summary -->
              <div class="mb-6">
                <h4 class="font-weight-bold mb-3">📋 สรุปข้อมูลรายงาน</h4>

                <v-simple-table dense class="summary-table">
                  <tbody>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        วิทยาลัย:
                      </td>
                      <td>{{ reportData.college_name }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        ปีที่รายงาน:
                      </td>
                      <td>
                        {{
                          reportData.report_time + "/" + reportData.report_year
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        งวดรายงาน:
                      </td>
                      <td>{{ currentPeriodLabel }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        เดือนที่รายงาน:
                      </td>
                      <td>{{ currentPeriodMonthsLabel }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        จำนวนพนักงาน:
                      </td>
                      <td>{{ reportData.total_employees }} คน</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        ผู้อำนวยการ:
                      </td>
                      <td>{{ reportData.director_name }}</td>
                    </tr>
                    <tr>
                      <td class="font-weight-bold text-right pr-4">
                        ตำแหน่ง:
                      </td>
                      <td>{{ reportData.director_title }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <v-divider class="my-4" />

              <!-- Budget Summary -->
              <div class="mb-6">
                <h4 class="font-weight-bold mb-3">💰 สรุปงบประมาณ</h4>

                <v-row>
                  <v-col cols="12" md="6" lg="3">
                    <v-card outlined>
                      <v-card-text class="pa-3">
                        <div class="text-caption text-grey">
                          งบประมาณทั้งงวด
                        </div>
                        <div class="text-h6 font-weight-bold text-primary">
                          ฿{{ formatCurrency(totalAllocatedBudget) }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6" lg="3">
                    <v-card outlined>
                      <v-card-text class="pa-3">
                        <div class="text-caption text-grey">
                          เบิกจ่ายทั้งงวด
                        </div>
                        <div class="text-h6 font-weight-bold text-green">
                          ฿{{ formatCurrency(totalDisbursedbBudget) }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6" lg="3">
                    <v-card outlined>
                      <v-card-text class="pa-3">
                        <div class="text-caption text-grey">
                          เหลือทั้งงวด
                        </div>
                        <div
                          class="text-h6 font-weight-bold"
                          :class="
                            totalRemainingBudget >= 0
                              ? 'text-orange'
                              : 'text-red'
                          "
                        >
                          ฿{{ formatCurrency(totalRemainingBudget) }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6" lg="3">
                    <v-card outlined>
                      <v-card-text class="pa-3">
                        <div class="text-caption text-grey">
                          อัตราเบิกจ่าย
                        </div>
                        <div class="text-h6 font-weight-bold text-info">
                          {{ overallDisbursePercentage }}%
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-4" />

              <!-- Files Summary -->
              <div class="mb-6">
                <h4 class="font-weight-bold mb-3">📄 ไฟล์ที่อัปโหลด</h4>

                <v-row class="mb-3">
                  <v-col cols="12" md="6">
                    <v-card outlined>
                      <v-card-text>
                        <div class="d-flex align-center mb-2">
                          <v-icon color="error" class="mr-2">
                            mdi-file-pdf
                          </v-icon>
                          <span class="font-weight-bold">หนังสือนำส่ง</span>
                        </div>
                        <div class="text-body2 text-grey ml-7">
                          {{ letterFile ? letterFile.name : "ไม่ได้เลือก" }}
                        </div>
                        <div
                          v-if="letterFile"
                          class="text-body2 text-grey ml-7"
                        >
                          {{ formatFileSize(letterFile.size) }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined>
                      <v-card-text>
                        <div class="d-flex align-center mb-2">
                          <v-icon color="error" class="mr-2">
                            mdi-file-pdf
                          </v-icon>
                          <span class="font-weight-bold">ไฟล์รายงาน</span>
                        </div>
                        <div class="text-body2 text-grey ml-7">
                          {{ reportFile ? reportFile.name : "ไม่ได้เลือก" }}
                        </div>
                        <div
                          v-if="reportFile"
                          class="text-body2 text-grey ml-7"
                        >
                          {{ formatFileSize(reportFile.size) }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-divider class="my-4" />

              <!-- Confirmation -->
              <v-alert
                type="warning"
                outlined
                class="mb-6"
                icon="mdi-alert-circle"
              >
                <div class="font-weight-bold mb-2">⚠️ ข้อมูลสำคัญ</div>
                <ul class="mb-0 ml-4">
                  <li>
                    ข้อมูลรายงานนี้จะถูกบันทึกในระบบและส่งไปยังส่วนกลาง
                  </li>
                  <li>
                    โปรดตรวจสอบความถูกต้องของข้อมูลทั้งหมดก่อนส่ง
                  </li>
                  <li>
                    ไฟล์ PDF ต้องมีลายเซ็นต์ของผู้อำนวยการเท่านั้น
                  </li>
                  <li>
                    ยอดคงเหลือจากเดือนก่อนหน้าจะถูกสะสมมาในการคำนวณ
                  </li>
                </ul>
              </v-alert>

              <v-checkbox
                v-model="confirmSubmit"
                :rules="[v => v || 'ต้องยอมรับเพื่อดำเนินการต่อ']"
                label="ฉันยืนยันว่าข้อมูลทั้งหมดถูกต้องและพร้อมที่จะส่งรายงาน"
                class="mb-4"
              />

              <v-divider class="my-4" />

              <div class="d-flex justify-space-between">
                <v-btn color="secondary" @click="currentStep = 4">
                  <v-icon class="mr-2">mdi-arrow-left</v-icon>
                  ย้อนกลับ
                </v-btn>
                <div class="d-flex gap-2">
                  <v-btn
                    color="success"
                    @click="submitReport"
                    :loading="isSubmitting"
                    :disabled="!confirmSubmit"
                  >
                    <v-icon class="mr-1">mdi-send</v-icon>
                    ส่งรายงาน
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-stepper-content>
      </v-stepper>
    </base-material-card>

    <!-- PDF Preview Dialog -->
    <v-dialog v-model="showPDFPreview" max-width="900px" fullscreen>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showPDFPreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>ตัวอย่างไฟล์ PDF</v-toolbar-title>
          <v-spacer />
          <v-btn
            v-if="reportFileDataURL"
            icon
            dark
            @click="downloadPreviewPDF"
            title="ดาวน์โหลด"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </v-toolbar>

        <iframe
          v-if="reportFileDataURL"
          :src="reportFileDataURL"
          style="width: 100%; border: none"
          height="600px"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewFilePdfDialog" max-width="80%">
      <v-card class="" elevation="2">
        <embed
          :src="
            'https://hrvec.ovec.go.th/HRvec_api/uploads/report/' +
              letter_reportFile
          "
          width="100%"
          height="700px"
        />
      </v-card>
    </v-dialog>

    <!-- Export Dialog -->
    <v-dialog v-model="showExportDialog" max-width="550px">
      <v-card class="rounded-xl">
        <v-card-title class="font-weight-bold grey lighten-5 pa-4">
          <v-icon color="error" class="mr-2">mdi-file-pdf-box</v-icon>
          รายงานข้อมูลบุคลากร (PDF)
        </v-card-title>
        <v-card-text class="pa-6">
          <v-form ref="exportForm" v-model="exportFormValid">
            <div class="mb-2">ข้อมูลหัวกระดาษ</div>
            <v-text-field
              v-model="exportOptions.collegeName"
              label="ชื่อหน่วยงาน/วิทยาลัย"
              outlined
              dense
              class="rounded-input mb-3"
              :rules="[v => !!v || 'จำเป็นต้องกรอก']"
            ></v-text-field>

            <div class="mb-2">ข้อมูลผู้ลงนาม</div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="exportOptions.directorName"
                  label="ชื่อผู้อำนวยการ"
                  outlined
                  dense
                  class="rounded-input"
                  :rules="[v => !!v || 'จำเป็นต้องกรอก']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="exportOptions.directorTitle"
                  label="ตำแหน่ง"
                  outlined
                  dense
                  class="rounded-input"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="mt-4 pa-4 rounded-lg border-dashed bg-light-grey">
              <v-checkbox
                v-model="exportOptions.includeStatistics"
                label="แนบหน้าสรุปสถิติอัตรากำลัง"
                dense
                hide-details
                class="mt-0"
              ></v-checkbox>
              <v-checkbox
                v-model="exportOptions.includeSignature"
                label="แสดงส่วนลงนามท้ายกระดาษ"
                dense
                hide-details
              ></v-checkbox>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="closeExportDialog">ยกเลิก</v-btn>
          <v-btn
            color="primary"
            rounded
            depressed
            @click="exportToPDF"
            :loading="exportLoading"
            :disabled="!exportFormValid"
          >
            <v-icon left>mdi-download</v-icon> ดาวน์โหลด PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Report Viewer -->

    <ReportViewer
      :report_govem_pdf="report_govem_pdf"
      v-if="showReportViewer"
      @close="closeReportViewer"
    />

    <!-- Snackbar for Messages -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
    >
      <v-icon class="mr-2">
        {{
          snackbar.color === "success" ? "mdi-check-circle" : "mdi-alert-circle"
        }}
      </v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn icon x-small v-bind="attrs" @click="snackbar.show = false">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ReportViewer from "./reportViewer.vue";
export default {
  props: {
    period_college: {
      type: [Array, Object]
    },
    user: {
      type: [Array, Object]
    },
    gov_employee_count: {},
    count_manpower: {},
    filteredEmployee: {}
  },
  components: {
    ReportViewer
  },

  // ========================================================================
  // DATA
  // ========================================================================
  data() {
    return {
      ApiKey: "HRvec2021",
      currentStep: 1,
      report_govem_pdf: {},
      // Form Validation
      reportFormValid: false,
      budgetFormValid: false,
      letterFormValid: false,
      reportFileFormValid: false,

      // Dialog
      viewFilePdfDialog: false,

      // Report Data
      reportData: {
        college_code: "",
        college_name: "",
        report_year: null,
        report_time: null,
        total_employees: 0,
        director_name: "",
        director_title: "ผู้อำนวยการวิทยาลัย",
        letter_date: null,
        letter_number: "",
        letter_description: "",
        report_description: "",
        reporting_period: "",
        period_allocated_budget: 0,
        monthly_budget: {}
      },

      exportFormValid: false,

      // Files
      letterFile: null,
      reportFile: null,
      reportFileDataURL: null,
      letter_reportFile_id: null,
      letter_reportFile: null,

      // UI State
      showEmployeeCountInfo: false,
      showPDFPreview: false,
      showCancelConfirm: false,
      showCancelApproveConfirm: false,
      confirmSubmit: false,
      isSubmitting: false,
      isSavingDraft: false,
      isPreviewingPDF: false,
      isDownloadingFile: false,
      isLoadingReport: false,
      isCancellingReport: false,
      existingReportWarning: {},

      availableYears: [],
      monthOptions: [
        { text: "มกราคม", value: 1 },
        { text: "กุมภาพันธ์", value: 2 },
        { text: "มีนาคม", value: 3 },
        { text: "เมษายน", value: 4 },
        { text: "พฤษภาคม", value: 5 },
        { text: "มิถุนายน", value: 6 },
        { text: "กรกฎาคม", value: 7 },
        { text: "สิงหาคม", value: 8 },
        { text: "กันยายน", value: 9 },
        { text: "ตุลาคม", value: 10 },
        { text: "พฤศจิกายน", value: 11 },
        { text: "ธันวาคม", value: 12 }
      ],

      // ===================================================================
      // periodMapping - ดึงจาก API แทน hardcode
      // โครงสร้าง: { [pcr_no]: { months: [10,11,12], label: "งวดที่ 1 ..." } }
      // ===================================================================
      periodMapping: {},

      // ข้อมูลงวดดิบจาก period_college_reporting
      reportingPeriods: [],

      // Snackbar
      snackbar: {
        show: false,
        message: "",
        color: "success",
        timeout: 3000
      },
      loading: false,
      exportOptions: {
        includeStatistics: true,
        includeSignature: true,
        directorName: "",
        directorTitle: "ผู้อำนวยการวิทยาลัย",
        collegeName: ""
      },
      showExportDialog: false,
      showReportViewer: false,
      exportLoading: false
    };
  },

  // ========================================================================
  // COMPUTED PROPERTIES
  // ========================================================================
  computed: {
    /**
     * เดือนที่ต้องรายงานในงวดปัจจุบัน
     * ดึงจาก periodMapping ตาม report_time (pcr_no)
     */
    periodMonths() {
      const periodTime = parseInt(this.reportData.report_time) || 0;
      const mapping = this.periodMapping[periodTime];
      if (!mapping || !mapping.months) return [];

      return mapping.months
        .map(monthValue => this.monthOptions.find(m => m.value === monthValue))
        .filter(m => m);
    },

    /**
     * ชื่องวดปัจจุบัน เช่น "งวดที่ 1 (ต.ค.-ธ.ค.)"
     */
    currentPeriodLabel() {
      const periodTime = parseInt(this.reportData.report_time) || 0;
      const mapping = this.periodMapping[periodTime];
      if (!mapping) return "ไม่พบข้อมูลงวด";
      return mapping.label;
    },

    /**
     * รายชื่อเดือนที่รายงาน เช่น "ประกอบด้วย เดือน ตุลาคม - ธันวาคม"
     */
    currentPeriodMonthsLabel() {
      const months = this.periodMonths;
      if (months.length === 0) return "";

      const monthNames = months.map(m => m.text);
      if (monthNames.length === 1) return "ประกอบด้วย เดือน " + monthNames[0];
      return (
        "ประกอบด้วย เดือน " +
        monthNames[0] +
        " - " +
        monthNames[monthNames.length - 1]
      );
    },

    totalAllocatedBudget() {
      let total = 0;
      for (const month of this.periodMonths) {
        const allocated =
          parseFloat(this.reportData.monthly_budget[month.value].allocated) ||
          0;
        total += allocated;
      }
      return total;
    },

    totalDisbursedbBudget() {
      let total = 0;
      for (const month of this.periodMonths) {
        const disbursed =
          parseFloat(this.reportData.monthly_budget[month.value].disbursed) ||
          0;
        total += disbursed;
      }
      return total;
    },

    totalRemainingBudget() {
      return (
        this.reportData.period_allocated_budget - this.totalDisbursedbBudget
      );
    },

    totalPeriodRemaining() {
      return (
        this.reportData.period_allocated_budget - this.totalDisbursedbBudget
      );
    },

    overallDisbursePercentage() {
      if (this.totalAllocatedBudget === 0) return 0;
      return Math.round(
        (this.totalDisbursedbBudget / this.totalAllocatedBudget) * 100
      );
    }
  },

  // ========================================================================
  // LIFECYCLE HOOKS
  // ========================================================================
  created() {
    this.reportData.letter_date = this.getTodayDate();
  },

  async mounted() {
    await this.loadReportingPeriods();
    await this.loadUserData();
    await this.checkExistingReport();
  },

  // ========================================================================
  // METHODS
  // ========================================================================
  methods: {
    // ====================================================================
    // โหลดข้อมูลงวดจากตาราง period_college_reporting (ใหม่)
    // ====================================================================

    /**
     * ดึงข้อมูลงวดจาก period_college_reporting API
     * แล้วสร้าง periodMapping แบบ dynamic
     */
    async loadReportingPeriods() {
      try {
        const periodCollegeId =
          this.period_college.period_college_id || "80104";

        const result = await this.$http.post("period_college_reporting.php", {
          ApiKey: this.ApiKey,
          action: "query",
          period_college_id: periodCollegeId
        });

        const periods = Array.isArray(result.data) ? result.data : [];
        this.reportingPeriods = periods;

        // สร้าง periodMapping จากข้อมูลจริง
        this.periodMapping = {};

        // ชื่อเดือนย่อภาษาไทย
        const monthShortNames = [
          "",
          "ม.ค.",
          "ก.พ.",
          "มี.ค.",
          "เม.ย.",
          "พ.ค.",
          "มิ.ย.",
          "ก.ค.",
          "ส.ค.",
          "ก.ย.",
          "ต.ค.",
          "พ.ย.",
          "ธ.ค."
        ];

        periods.forEach(p => {
          const pcrNo = parseInt(p.pcr_no);
          const months = (p.pcr_months || "")
            .split(",")
            .map(m => parseInt(m.trim()))
            .filter(m => !isNaN(m) && m >= 1 && m <= 12)
            .sort((a, b) => a - b);

          // สร้าง label อัตโนมัติ
          let label = "";
          if (p.pcr_comment) {
            label = p.pcr_comment;
          } else if (months.length > 0) {
            const firstMonth = monthShortNames[months[0]] || "";
            const lastMonth = monthShortNames[months[months.length - 1]] || "";
            label =
              months.length === 1
                ? `งวดที่ ${pcrNo} (${firstMonth})`
                : `งวดที่ ${pcrNo} (${firstMonth}-${lastMonth})`;
          } else {
            label = `งวดที่ ${pcrNo}`;
          }

          this.$set(this.periodMapping, pcrNo, {
            months: months,
            label: label,
            pcr_enable: p.pcr_enable,
            pcr_start: p.pcr_start,
            pcr_stop: p.pcr_stop
          });
        });

        // ถ้าไม่มีข้อมูลงวดจาก DB ให้ fallback เป็น default 4 งวด
        if (periods.length === 0) {
          console.warn(
            "ไม่พบข้อมูลงวดจาก period_college_reporting, ใช้ค่า default"
          );
          this.periodMapping = {
            1: { months: [10, 11, 12], label: "งวดที่ 1 (ต.ค.-ธ.ค.)" },
            2: { months: [1, 2, 3], label: "งวดที่ 2 (ม.ค.-มี.ค.)" },
            3: { months: [4, 5, 6], label: "งวดที่ 3 (เม.ย.-มิ.ย.)" },
            4: { months: [7, 8, 9], label: "งวดที่ 4 (ก.ค.-ก.ย.)" }
          };
        }
      } catch (error) {
        console.error("Error loading reporting periods:", error);
        // Fallback to default
        this.periodMapping = {
          1: { months: [10, 11, 12], label: "งวดที่ 1 (ต.ค.-ธ.ค.)" },
          2: { months: [1, 2, 3], label: "งวดที่ 2 (ม.ค.-มี.ค.)" },
          3: { months: [4, 5, 6], label: "งวดที่ 3 (เม.ย.-มิ.ย.)" },
          4: { months: [7, 8, 9], label: "งวดที่ 4 (ก.ค.-ก.ย.)" }
        };
      }
    },

    // ====================================================================
    // HELPER METHODS
    // ====================================================================

    async deleteSubmittedReport() {
      this.isCancellingReport = true;
      try {
        const response = await this.$http.post(
          "government_employee_report_delete.php",
          {
            ApiKey: this.ApiKey,
            report_id: this.existingReportWarning.id,
            user_id: this.user.user_ID || "system"
          }
        );

        if (!response.data || !response.data.status) {
          throw new Error(response.data.message || "ไม่สามารถลบรายการได้");
        }

        this.existingReportWarning = {};
        this.showCancelConfirm = false;
        this.resetForm();
        await this.loadReportingPeriods();
        await this.loadUserData();
        await this.checkExistingReport();
        this.showSnackbar(
          "ลบรายการสำเร็จ คุณสามารถสร้างรายการใหม่ได้",
          "success"
        );

        setTimeout(() => {
          this.checkExistingReport();
        }, 2000);
      } catch (error) {
        console.error("Error deleting report:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการลบ: " + error.message, "error");
      } finally {
        this.isCancellingReport = false;
      }
    },

    async requestCancelApprovedReport() {
      this.isCancellingReport = true;
      try {
        const response = await this.$http.post(
          "government_employee_report.cancel.php",
          {
            ApiKey: this.ApiKey,
            report_id: this.existingReportWarning.id,
            action: "request_cancel",
            user_id: this.user.user_ID || "system"
          }
        );

        if (!response.data || !response.data.status) {
          throw new Error(response.data.message || "ไม่สามารถขอยกเลิกได้");
        }

        this.showCancelApproveConfirm = false;
        this.showSnackbar("ขอยกเลิกสำเร็จ รอการอนุมัติจากส่วนกลาง", "success");

        setTimeout(() => {
          this.checkExistingReport();
        }, 2000);
      } catch (error) {
        console.error("Error requesting cancellation:", error);
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการขอยกเลิก: " + error.message,
          "error"
        );
      } finally {
        this.isCancellingReport = false;
      }
    },

    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const day = String(today.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    getStatusColor(status) {
      const colors = {
        draft: "grey",
        submitted: "blue",
        approved: "green",
        rejected: "red",
        cancelled: "orange"
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        draft: "ร่าง",
        submitted: "รอการอนุมัติ",
        approved: "อนุมัติแล้ว",
        rejected: "ปฏิเสธ",
        cancelled: "ยกเลิก"
      };
      return texts[status] || status;
    },

    showSnackbar(message, color = "success") {
      this.snackbar = { show: true, message, color, timeout: 3000 };
    },

    // ====================================================================
    // MONTHLY BUDGET METHODS
    // ====================================================================

    getMonthlyBudgetValue(monthValue, field) {
      const monthBudget = this.reportData.monthly_budget[monthValue];
      if (!monthBudget) return field === "notes" ? "" : 0;
      return monthBudget[field] || (field === "notes" ? "" : 0);
    },

    ensureMonthlyBudgetExists(monthValue) {
      if (!this.reportData.monthly_budget[monthValue]) {
        this.$set(this.reportData.monthly_budget, monthValue, {
          allocated: 0,
          disbursed: 0,
          remaining: 0,
          notes: ""
        });
      }
    },

    onMonthlyDisbursedChange(monthValue, value) {
      this.ensureMonthlyBudgetExists(monthValue);
      const numValue = parseFloat(value) || 0;
      this.reportData.monthly_budget[monthValue].disbursed = numValue;
      this.calculateMonthlyRemaining(monthValue);
    },

    onMonthlyNotesChange(monthValue, value) {
      this.ensureMonthlyBudgetExists(monthValue);
      this.reportData.monthly_budget[monthValue].notes = value || "";
    },

    // ====================================================================
    // BUDGET CALCULATION METHODS
    // ====================================================================

    updateAllMonthsAllocatedBudget() {
      const monthCount = this.periodMonths.length;
      if (monthCount === 0) return;

      const allocatedPerMonth =
        parseFloat(this.reportData.period_allocated_budget) / monthCount;

      for (const month of this.periodMonths) {
        this.ensureMonthlyBudgetExists(month.value);
        this.reportData.monthly_budget[month.value].allocated =
          Math.round(allocatedPerMonth * 100) / 100;
      }
    },

    getPreviousMonthRemaining(currentMonthValue) {
      const periodTime = parseInt(this.reportData.report_time) || 0;
      const mapping = this.periodMapping[periodTime];
      if (!mapping || !mapping.months) return 0;

      const months = mapping.months;
      const monthIndex = months.indexOf(currentMonthValue);

      if (monthIndex <= 0) return 0;

      const previousMonth = months[monthIndex - 1];
      return (
        parseFloat(this.reportData.monthly_budget[previousMonth].remaining) || 0
      );
    },

    getTotalAvailableBudget(monthValue) {
      const allocated =
        parseFloat(this.reportData.monthly_budget[monthValue].allocated) || 0;
      const previousRemaining = this.getPreviousMonthRemaining(monthValue);
      return allocated + previousRemaining;
    },

    calculateMonthlyRemaining(monthValue) {
      this.ensureMonthlyBudgetExists(monthValue);
      const budget = this.reportData.monthly_budget[monthValue];
      const totalAvailable = this.getTotalAvailableBudget(monthValue);
      budget.remaining = totalAvailable - (parseFloat(budget.disbursed) || 0);
    },

    getDisbursePercentage(monthValue) {
      const totalAvailable = this.getTotalAvailableBudget(monthValue);
      if (totalAvailable === 0) return 0;
      return Math.round(
        ((parseFloat(this.reportData.monthly_budget[monthValue].disbursed) ||
          0) /
          totalAvailable) *
          100
      );
    },

    getRemainingStyling(monthValue) {
      const remaining =
        parseFloat(this.reportData.monthly_budget[monthValue].remaining) || 0;
      return remaining < 0 ? "border-color: red !important; color: red;" : "";
    },

    // ====================================================================
    // INITIALIZATION METHODS
    // ====================================================================

    initializeYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.availableYears = [];
      for (let i = 0; i <= 5; i++) {
        this.availableYears.push(currentYear - i);
      }
    },

    initializeMonthlyBudget() {
      const periodTime = parseInt(this.reportData.report_time) || 0;
      const mapping = this.periodMapping[periodTime];
      const months = mapping ? mapping.months : [];

      this.reportData.monthly_budget = {};

      months.forEach(monthValue => {
        this.$set(this.reportData.monthly_budget, monthValue, {
          allocated: 0,
          disbursed: 0,
          remaining: 0,
          notes: ""
        });
      });
    },

    // ====================================================================
    // DATA LOADING METHODS
    // ====================================================================

    async loadUserData() {
      try {
        this.reportData.college_code = this.user.user_code || "";
        this.reportData.college_name = this.user.college_name || "";
        this.reportData.director_name = this.user.collegeinfo_director || "";
        this.reportData.director_title =
          "ผู้อำนวยการ" + this.user.college_name || "";

        this.reportData.report_year =
          this.period_college.period_college_year || "";
        this.reportData.report_time =
          this.period_college.period_college_times || "";
        this.reportData.reporting_period =
          this.period_college.period_reporting || "";

        this.reportData.total_employees = this.gov_employee_count || 0;
        this.initializeYears();
        this.initializeMonthlyBudget();
      } catch (error) {
        console.error("Error loading user data:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้", "error");
      }
    },

    async checkExistingReport() {
      try {
        const response = await this.$http.post(
          "government_employee_report.read.php",
          {
            ApiKey: this.ApiKey,
            college_code: this.user.user_code,
            report_year: this.period_college.period_college_year,
            report_time: this.period_college.period_college_times
          }
        );
        this.existingReportWarning = response.data || {};
      } catch (error) {
        console.error("Error checking existing report:", error);
      }
    },

    // ====================================================================
    // FILE HANDLING METHODS
    // ====================================================================

    validateLetterFile() {
      if (!this.letterFile) return;

      const maxSize = 10 * 1024 * 1024;
      if (this.letterFile.size > maxSize) {
        this.showSnackbar("ไฟล์ใหญ่เกินไป (สูงสุด 10MB)", "error");
        this.letterFile = null;
        return false;
      }

      if (this.letterFile.type !== "application/pdf") {
        this.showSnackbar("เฉพาะไฟล์ PDF เท่านั้น", "error");
        this.letterFile = null;
        return false;
      }

      return true;
    },

    validateReportFile() {
      if (!this.reportFile) return;

      const maxSize = 10 * 1024 * 1024;
      if (this.reportFile.size > maxSize) {
        this.showSnackbar("ไฟล์ใหญ่เกินไป (สูงสุด 10MB)", "error");
        this.reportFile = null;
        return false;
      }

      if (this.reportFile.type !== "application/pdf") {
        this.showSnackbar("เฉพาะไฟล์ PDF เท่านั้น", "error");
        this.reportFile = null;
        return false;
      }

      return true;
    },

    clearLetterFile() {
      this.letterFile = null;
      if (this.$refs.letterFileInput) {
        this.$refs.letterFileInput.clearableCallback();
      }
    },

    clearReportFile() {
      this.reportFile = null;
      this.reportFileDataURL = null;
      if (this.$refs.reportFileInput) {
        this.$refs.reportFileInput.clearableCallback();
      }
    },

    previewReportPDF() {
      if (!this.reportFile) return;

      this.isPreviewingPDF = true;
      const reader = new FileReader();

      reader.onload = e => {
        this.reportFileDataURL = e.target.result;
        this.showPDFPreview = true;
        this.isPreviewingPDF = false;
      };

      reader.onerror = () => {
        this.showSnackbar("ไม่สามารถอ่านไฟล์ PDF", "error");
        this.isPreviewingPDF = false;
      };

      reader.readAsDataURL(this.reportFile);
    },

    downloadPreviewPDF() {
      const link = document.createElement("a");
      link.href = this.reportFileDataURL;
      link.download = this.reportFile.name;
      link.click();
    },

    async downloadFile(reportId, fileType) {
      this.letter_reportFile_id = null;
      this.letter_reportFile = null;
      let reportFile;
      if (fileType === "letter") {
        reportFile = reportId.letterFile;
      } else if (fileType === "report") {
        reportFile = reportId.report_file;
      }
      console.log("Downloading file:", reportFile);
      this.letter_reportFile_id = reportId.id;
      this.letter_reportFile = reportFile;
      this.viewFilePdfDialog = true;
    },

    // ====================================================================
    // STEP NAVIGATION METHODS
    // ====================================================================

    proceedToStep2() {
      if (!this.$refs.reportForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วน", "warning");
        return;
      }
      this.currentStep = 2;
    },

    proceedToStep3() {
      if (!this.$refs.budgetForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลงบประมาณให้ครบถ้วน", "warning");
        return;
      }

      this.currentStep = 3;
    },

    proceedToStep4() {
      if (!this.$refs.letterForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วน", "warning");
        return;
      }
      if (!this.validateLetterFile()) {
        this.showSnackbar("ไฟล์หนังสือนำส่งไม่ถูกต้อง", "error");
        return;
      }
      this.currentStep = 4;
    },

    proceedToStep5() {
      if (!this.$refs.reportFileForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วน", "warning");
        return;
      }
      if (!this.validateReportFile()) {
        this.showSnackbar("ไฟล์รายงานไม่ถูกต้อง", "error");
        return;
      }
      this.currentStep = 5;
    },

    onStepChange(step) {},

    // ====================================================================
    // CANCEL REPORT METHODS
    // ====================================================================

    async cancelReport() {
      await this.deleteSubmittedReport();
    },

    async cancelReportApprove() {
      if (this.existingReportWarning.status === "approved") {
        await this.requestCancelApprovedReport();
      }
    },

    // ====================================================================
    // SUBMIT REPORT METHODS
    // ====================================================================

    async submitReport() {
      this.isSubmitting = true;

      try {
        let reportId = "";
        if (
          this.existingReportWarning.status === "rejected" ||
          this.existingReportWarning.status === "cancelled"
        ) {
          reportId = await this.updateOrUpdateReport("submitted");
        } else {
          reportId = await this.createOrUpdateReport("submitted");
        }

        await this.uploadLetterFile(reportId);
        await this.uploadReportFile(reportId);

        this.showSnackbar("ส่งรายงานสำเร็จ", "success");

        setTimeout(() => {
          this.resetForm();
          this.currentStep = 1;
          this.checkExistingReport();
        }, 2000);
      } catch (error) {
        console.error("Error submitting report:", error);
        this.showSnackbar(
          "เกิดข้อผิดพลาดในการส่งรายงาน: " + error.message,
          "error"
        );
      } finally {
        this.isSubmitting = false;
      }
    },

    async createOrUpdateReport(status) {
      const payload = {
        ApiKey: this.ApiKey,
        college_code: this.reportData.college_code,
        report_year: this.reportData.report_year,
        report_time: this.reportData.report_time,
        report_month: this.reportData.report_month || 0,
        total_employees: this.reportData.total_employees,
        director_name: this.reportData.director_name,
        director_title: this.reportData.director_title,
        college_name: this.reportData.college_name,
        created_by: this.user.user_ID || "system",
        status: status,
        reporting_period: this.currentPeriodLabel,
        allocated_budget: this.reportData.period_allocated_budget,
        disbursed_budget: this.totalDisbursedbBudget,
        remaining_budget: this.totalRemainingBudget,
        monthly_budget: JSON.stringify(this.reportData.monthly_budget)
      };

      const response = await this.$http.post(
        "government_employee_report.insert.php",
        payload
      );
      console.log("Create/Update Report Response:", response);

      if (!response.data || !response.data.status) {
        throw new Error(response.data.message || "ไม่สามารถบันทึกรายงาน");
      }

      return response.data.id;
    },

    async updateOrUpdateReport(status) {
      const payload = {
        ApiKey: this.ApiKey,
        id: this.existingReportWarning.id,
        college_code: this.reportData.college_code,
        report_year: this.reportData.report_year,
        report_time: this.reportData.report_time,
        report_month: this.reportData.report_month || 0,
        total_employees: this.reportData.total_employees,
        director_name: this.reportData.director_name,
        director_title: this.reportData.director_title,
        status: status,
        cancellation_reason: "",
        rejection_reason: "",
        allocated_budget: this.totalAllocatedBudget,
        disbursed_budget: this.totalDisbursedbBudget,
        remaining_budget: this.totalRemainingBudget,
        monthly_budget: JSON.stringify(this.reportData.monthly_budget)
      };

      const response = await this.$http.post(
        "government_employee_report.update.php",
        payload
      );
      if (!response.data || !response.data.status) {
        throw new Error(response.data.message || "ไม่สามารถบันทึกรายงาน");
      }

      return this.existingReportWarning.id;
    },

    async uploadLetterFile(reportId) {
      if (!this.letterFile) return;

      const formData = new FormData();
      formData.append("ApiKey", this.ApiKey);
      formData.append("report_id", reportId);
      formData.append("user_id", this.user.user_ID || "system");
      formData.append("letterFile", this.letterFile);

      const response = await this.$http.post(
        "government_employee_report.upload.php",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      if (!response.data || !response.data.status) {
        throw new Error(
          response.data.message || "ไม่สามารถอัปโหลดไฟล์หนังสือนำส่ง"
        );
      }
    },

    async uploadReportFile(reportId) {
      if (!this.reportFile) return;

      const formData = new FormData();
      formData.append("ApiKey", this.ApiKey);
      formData.append("report_id", reportId);
      formData.append("user_id", this.user.user_ID || "system");
      formData.append("pdf_file", this.reportFile);

      const response = await this.$http.post(
        "government_employee_report.upload.php",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      if (!response.data || !response.data.status) {
        throw new Error(response.data.message || "ไม่สามารถอัปโหลดไฟล์รายงาน");
      }
    },

    // ====================================================================
    // RESET FORM METHOD
    // ====================================================================

    resetForm() {
      this.reportData = {
        college_code: this.user.user_code || "",
        college_name: this.user.college_name || "",
        report_year: null,
        report_time: null,
        total_employees: 0,
        director_name: this.user.full_name || "",
        director_title: "ผู้อำนวยการวิทยาลัย",
        letter_date: this.getTodayDate(),
        letter_number: "",
        letter_description: "",
        report_description: "",
        reporting_period: "",
        period_allocated_budget: 0,
        monthly_budget: {}
      };

      this.letterFile = null;
      this.reportFile = null;
      this.reportFileDataURL = null;
      this.confirmSubmit = false;

      if (this.$refs.reportForm) this.$refs.reportForm.resetValidation();
      if (this.$refs.budgetForm) this.$refs.budgetForm.resetValidation();
      if (this.$refs.letterForm) this.$refs.letterForm.resetValidation();
      if (this.$refs.reportFileForm)
        this.$refs.reportFileForm.resetValidation();
    },

    async downloadLetterTemplate() {
      try {
        this.isDownloadingFile = true;
        const response =
          "https://hrvec.ovec.go.th/docEx/หนังสือนำส่งรายงานข้อมูลพนักงานราชการ.docx";
        const link = document.createElement("a");
        link.href = response;
        link.download = "หนังสือนำส่งรายงานพนักงานราชการ.docx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(response);
      } catch (error) {
        console.error("Error downloading template:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการดาวน์โหลดไฟล์", "error");
      } finally {
        this.isDownloadingFile = false;
      }
    },

    openExportDialog() {
      if (this.filteredEmployee.length === 0) {
        this.showSnackbar("ไม่มีข้อมูลในการส่งออก", "warning");
        return;
      }

      this.exportOptions.directorName = this.user.collegeinfo_director || "";
      this.exportOptions.collegeName =
        this.user.college_name || "วิทยาลัยเทคนิค";
      this.showExportDialog = true;
    },

    async exportToPDF() {
      if (!this.$refs.exportForm.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วน", "warning");
        return;
      }

      this.exportLoading = true;

      try {
        const employees = this.filteredEmployee.map(emp => ({
          ...emp,
          full_name: `${emp.title_s || ""} ${emp.frist_name ||
            ""} ${emp.last_name || ""}`.trim()
        }));

        const payload = {
          ApiKey: this.ApiKey,
          college_code: this.user.user_code || "",
          college_name: this.exportOptions.collegeName,
          director_name: this.exportOptions.directorName,
          director_title: this.exportOptions.directorTitle,
          report_date: new Date().toISOString().split("T")[0],
          employees: JSON.stringify(employees),
          include_statistics: this.exportOptions.includeStatistics ? "1" : "0",
          include_signature: this.exportOptions.includeSignature ? "1" : "0"
        };

        let response = await this.$http.post(
          "export_goverment_employee_pdf.php",
          payload
        );

        if (response.data && response.data.success) {
          this.report_govem_pdf = response.data;
          this.showReportViewer = true;
          this.showSnackbar("ส่งออก PDF สำเร็จ", "success");
          this.showExportDialog = false;
        } else {
          this.showSnackbar(
            response.data.message || "ส่งออก PDF ไม่สำเร็จ",
            "error"
          );
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออก", "error");
      } finally {
        this.exportLoading = false;
      }
    },

    closeReportViewer() {
      this.showReportViewer = false;
      this.report_govem_pdf = {};
    },
    closeExportDialog() {
      this.showExportDialog = false;
    }
  }
};
</script>

<style scoped>
.government-report-container {
  padding: 24px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.monthly-budget-card {
  transition: all 0.3s ease;
  height: 100%;
}

.monthly-budget-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-section {
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
}

.summary-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.summary-table {
  width: 100%;
}

.summary-table tbody tr {
  border-bottom: 1px solid #eee;
}

.summary-table tbody tr:last-child {
  border-bottom: none;
}

.summary-table td {
  padding: 12px 8px;
  vertical-align: middle;
}

.bg-light {
  background-color: #f5f5f5;
}

.bg-blue-lighten-5 {
  background-color: #e3f2fd;
}

.bg-orange-lighten-5 {
  background-color: #ffe0b2;
}

.bg-grey-lighten-3 {
  background-color: #f5f5f5;
}

.gap-2 {
  gap: 8px;
}

.justify-end {
  justify-content: flex-end;
}

.justify-space-between {
  justify-content: space-between;
}

.text-green {
  color: #4caf50;
}

.text-orange {
  color: #ff9800;
}

.text-red {
  color: #f44336;
}

.text-info {
  color: #2196f3;
}

.v-stepper__header {
  box-shadow: none;
}

.v-stepper__content {
  padding: 0;
}

.rounded {
  border-radius: 4px;
}
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4);
  }
  70% {
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    transform: scale(1);
  }
}

.animate-pulse {
  animation: pulse 2s infinite;
}

.pulse-button {
  border: 2px solid #fff;
}

.custom-stepper {
  border: 3px solid #1a237e; /* สีกรมท่า */
}

/* เน้นส่วนงวดรายงานให้เด่นขึ้นในหน้าแรก */
.current-period-box {
  background: #fff3e0;
  border: 2px dashed #fb8c00;
  font-size: 1.2rem;
}
</style>
